/* eslint-disable @typescript-eslint/naming-convention */
// DentalHistory.tsx
import DateTextField from '@brands/Components/Inputs/MuiDateTextField/DateTextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { getUserById } from '@brands/services/identity/getUserById';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { setExternalForms } from '@brands/store/slices/patientFormSlice';
import { checkExternalFormAnswers } from '@brands/Utils/checkExternalForms';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { getSecondary } from '@brands/Utils/getPrimaryColor';
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import TextField from '../../../Components/Inputs/MuiTextField/TextField';
import { Loading } from '../../../Components/LoadingSpinner/Loading';
import { useAppDispatch, useAppSelector } from '../../../hooks/useReduxHook';
import styles from './styles.module.scss';
import { visitOptions } from './utils/questions';
import { DentalHistoryProps, DentalHistorySubmit } from './utils/types';
import { dentalHistoryValidationSchema } from './utils/validationSchema';

const DentalHistory = ({ isProfileCard, formRef, setIsDirty }: DentalHistoryProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [objectId, setObjectId] = useState<string | null>(null);
  const { selectedPatient } = useAppSelector(selectPatientForm);
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const { userInfo } = useAppSelector(selectAuth);
  const [guardianPatient, setGuardianPatient] = useState<UserProfile>();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<DentalHistorySubmit>({
    last_dental_visit: '',
    who_did_you_see: '',
    visit_type: '',
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const [savedLastDentalVisit, setSavedLastDentalVisit] = useState('');
  const [savedWhoDidYouSee, setSavedWhoDidYouSee] = useState('');

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    clearErrors,
    getValues,
    reset,
  } = useForm<DentalHistorySubmit>({
    mode: 'onChange',
    resolver: yupResolver(dentalHistoryValidationSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    const checkAllFormFilled = (): void => {
      const currentValues = watch();
      const anyChange = !isEqual(initialValues, currentValues);
      setIsDirty?.(anyChange);
      // Enabled on any change, valid or not, so a click on Save reaches
      // onInvalid and its toast instead of silently doing nothing.
      setIsDisabled(anyChange);
    };
    checkAllFormFilled();
  }, [watch()]);

  const getGuardianPatient = async (): Promise<UserProfile | undefined> => {
    if (
      userInfo.role.name === UserRoleName.SuperAdmin ||
      userInfo.role.name === UserRoleName.OrganizationAdmin
    ) {
      if (currentPatient.guardian_id !== null) {
        const guardianResponse = await getUserById(currentPatient?.guardian_id as number);
        setGuardianPatient(guardianResponse);
        return guardianResponse;
      }
    }
  };

  const checkExternalForms = async (): Promise<void> => {
    const [checkProfile, checkMedicalHistory, checkMedicalConditionsFormAnswer, checkDentalHistoryFormAnswer] =
      await checkExternalFormAnswers(
          userInfo.role.name === UserRoleName.SuperAdmin ||
          userInfo.role.name === UserRoleName.OrganizationAdmin
          ? (guardianPatient ?? (currentPatient as UserProfile))
          : userInfo,
        currentPatient,
      );
    const formsToSet = [];

    if (!checkProfile) formsToSet.push('patient_profile');
    if (!checkMedicalHistory) formsToSet.push('patient_medical_history');
    if (!checkMedicalConditionsFormAnswer) formsToSet.push('patient_medical_conditions');
    if (!checkDentalHistoryFormAnswer) formsToSet.push('patient_dental_history');

    dispatch(setExternalForms(formsToSet));
  };

  const onSubmit = async (data: DentalHistorySubmit): Promise<void | null> => {
    setLoading(true);
    try {
      const currentUserId =
        userInfo.role.name === UserRoleName.SuperAdmin ||
        userInfo.role.name === UserRoleName.OrganizationAdmin
          ? currentPatient.guardian_id !== null
            ? (guardianPatient as UserProfile).id
            : (currentPatient as UserProfile).id
          : currentPatient.id;

      const updatedData = {
        ...data,
        user_id: currentUserId,
      };

      if (!objectId) {
        await addClinicalData('dental_history', updatedData);
      } else {
        await updateClinicalData('dental_history', objectId, updatedData);
      }
      await checkExternalForms();

      const snapshot = getValues();
      setInitialValues(snapshot);
      reset(snapshot);
      setSavedLastDentalVisit('');
      setSavedWhoDidYouSee('');
      setLoading(false);
      showSnackbar('Dental history saved successfully!', 'success');
    } catch (error) {
      displayErrorDetails(error);
    }
  };

  const onInvalid = (e: any): void => {
    const error = Object.values(e)[0] as any;
    if (error) {
      const errorMessage = error.message || 'Please fill out all required fields.';
      showSnackbar(errorMessage, 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchDentalHistory = async (): Promise<void> => {
      if (!selectedPatient?.value) return;
      const fetchedGuardian = await getGuardianPatient();
      const currentUserId =
        userInfo.role.name === UserRoleName.SuperAdmin ||
        userInfo.role.name === UserRoleName.OrganizationAdmin
          ? currentPatient.guardian_id !== null
            ? (fetchedGuardian as UserProfile).id
            : (currentPatient as UserProfile).id
          : currentPatient.id;
      setLoading(true);
      try {
        const { dental_history } = await listClinicalData(currentUserId);
        if (dental_history) {
          setObjectId(dental_history.id.toString());

          setValue('last_dental_visit', dental_history.last_dental_visit ?? '', { shouldValidate: true });
          setValue('who_did_you_see', dental_history.who_did_you_see ?? '', { shouldValidate: true });
          setValue('visit_type', dental_history.visit_type ?? '', { shouldValidate: true });

          const snapshot = {
            last_dental_visit: dental_history.last_dental_visit ?? '',
            who_did_you_see: dental_history.who_did_you_see ?? '',
            visit_type: dental_history.visit_type ?? '',
          };

          setInitialValues(snapshot);
          reset(snapshot);
        }
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setLoading(false);
      }
    };
    fetchDentalHistory();
  }, [selectedPatient?.value]);

  if (loading) return <Loading dotted />;

  return (
    <div className={styles.ProfileContainer}>
      <div className={`${styles.ProfileDiv} ${styles.medicalContainer}`}>
        <form
          ref={formRef}
          className={styles.formContainer}
          style={{ width: isProfileCard ? '100%' : '', marginRight: isProfileCard ? '0px' : '' }}
          onSubmit={handleSubmit(onSubmit, onInvalid)}
        >
          <div className={styles.wrapper}>
            <div className={styles.formSection}>
              <div className={`${styles.formQuestion} fs-unmask`}>
                When is the last time you saw a dentist in person?
              </div>
              <div className={styles.formContent}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    {...register('last_dental_visit')}
                    label="Last dental appointment"
                    value={
                      watch('last_dental_visit') && watch('last_dental_visit') !== 'dontKnow'
                        ? dayjs(watch('last_dental_visit'))
                        : null
                    }
                    data-testid="denta-history-last-dental-visit"
                    onChange={(newValue: Dayjs | null) => {
                      if (errors.last_dental_visit) clearErrors('last_dental_visit');
                      const formattedDate = newValue ? dayjs(newValue).format('MM/DD/YYYY') : '';
                      setValue('last_dental_visit', formattedDate, { shouldValidate: true });
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'medium',
                        className: `${styles.providerSelectVisitDateInputField} ${styles.inputWrap} fs-unmask`,
                        inputProps: {
                          readOnly: false,
                          className: `${styles.profileInputClass} fs-exclude`,
                        },
                        error: !!errors.last_dental_visit,
                        helperText: errors.last_dental_visit?.message || '',
                      },
                    }}
                  />
                </LocalizationProvider>
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxInput}
                    checked={watch('last_dental_visit') === 'dontKnow'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;

                      if (isChecked) {
                        setSavedLastDentalVisit(watch('last_dental_visit') || '');
                        setValue('last_dental_visit', 'dontKnow', { shouldValidate: true });
                      } else {
                        setValue('last_dental_visit', savedLastDentalVisit || '', { shouldValidate: true });
                      }

                      if (errors.last_dental_visit) clearErrors('last_dental_visit');
                    }}
                    sx={{ color: 'rgba(23, 36, 51, 0.87)', '&.Mui-checked': { color: getSecondary() } }}
                    name="not_sure"
                    value="not_sure"
                  />
                  <label className={styles.checkboxLabel} htmlFor="not_sure">
                    Not sure
                  </label>
                </div>
              </div>
              <div className={styles.formContent}>
                <TextField
                  {...register('who_did_you_see')}
                  value={watch('who_did_you_see') === 'dontKnow' ? '' : watch('who_did_you_see')}
                  label="Dentist Seen"
                  errors={errors.who_did_you_see || {}}
                  onChange={(val) => setValue('who_did_you_see', val.target.value, { shouldValidate: true })}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.inputWrap}`}
                />
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxInput}
                    checked={watch('who_did_you_see') === 'dontKnow'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;

                      if (isChecked) {
                        setSavedWhoDidYouSee(watch('who_did_you_see') || '');
                        setValue('who_did_you_see', 'dontKnow', { shouldValidate: true });
                      } else {
                        setValue('who_did_you_see', savedWhoDidYouSee || '', { shouldValidate: true });
                      }

                      if (errors.who_did_you_see) clearErrors('who_did_you_see');
                    }}
                    sx={{ color: 'rgba(23, 36, 51, 0.87)', '&.Mui-checked': { color: getSecondary() } }}
                    name="not_sure"
                    value="not_sure"
                    data-testid="not-sure-checkbox"
                  />
                  <label className={styles.checkboxLabel} htmlFor="not_sure">
                    Not sure
                  </label>
                </div>
              </div>
              <div className={styles.formContent}>
                <MUISelectInput
                  {...register('visit_type')}
                  data-testid="complete-profile-visit-type"
                  label="Reason for visit (optional)"
                  errors={errors.visit_type ? errors : {}}
                  name="visit_type"
                  onChange={(selectedValue: string) => {
                    if (errors.visit_type) clearErrors('visit_type');
                    setValue('visit_type', selectedValue, { shouldValidate: true });
                  }}
                  value={watch('visit_type')}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.inputWrap}`}
                  options={visitOptions}
                />
              </div>
            </div>
            <div className={styles.btnContainer}>
              <button type="submit" className={styles.saveButton} disabled={!isDisabled || loading}>
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </div>
  );
};

export default DentalHistory;
